import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'
import { PopupModal } from 'react-calendly'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Text } from 'react-native'
import type { SvgProps } from 'react-native-svg'
import { ClipPath, Defs, G, Path, Svg } from 'react-native-svg'
import { tv } from 'tailwind-variants'
import { P, match } from 'ts-pattern'
import * as z from 'zod'

import Spinner from '@nxt-insurance/ui-kit/components/Spinner'
import { body1 } from '@nxt-insurance/ui-kit/core/typography'
import type { Color, Space } from '@nxt-insurance/ui-kit/types'

import type { HorizontalAlignment } from '@/cms/helperTypes'
import { Block } from '@/components/renderers/Block'
import { ButtonRenderer } from '@/components/renderers/ButtonRenderer'
import { Card } from '@/components/renderers/Card'
import { CheckMarkList } from '@/components/renderers/CheckMarkList'
import { ChipsRow } from '@/components/renderers/ChipsRow'
import { ConfigurableTypography } from '@/components/renderers/ConfigurableTypography'
import { CtaButton } from '@/components/renderers/CtaButton'
import { FlexibleImage } from '@/components/renderers/FlexibleImage'
import type { ProductCardSimpleSchema } from '@/components/renderers/ProductCardSimple'
import { RestrictedContent } from '@/components/renderers/RestrictedContent'
import { Spacer } from '@/components/renderers/Spacer'
import { DOMAIN } from '@/config/env'
import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { reportErrorMessage } from '@/services/errorReporting'
import { getLocaleParts } from '@/utils/locale'
import type { Language } from '@constants/format'
import type {
  PortableTextComponentProps,
  PortableTextListComponent,
  PortableTextListItemComponent,
  PortableTextMarkComponentProps,
  PortableTextTypeComponentProps,
  UnknownNodeType,
} from '@portabletext/react'
import { PortableText } from '@portabletext/react'
import type { ArbitraryTypedObject, PortableTextBlock } from '@portabletext/types'

import { LinkRenderer, LinkRendererSimple } from './LinkRenderer'

const ModalLink = dynamic(
  async () => import('@/components/renderers/ModalLink').then((mod) => mod.ModalLink),
  {
    ssr: false,
  }
)
const AppDownloadButtons = dynamic(async () =>
  import('@/components/renderers/AppDowloadButtons').then((mod) => mod.AppDownloadButtons)
)
const Carousel = dynamic(async () =>
  import('@/components/renderers/Carousel').then((mod) => mod.Carousel)
)
const FooterLinkList = dynamic(async () =>
  import('@/components/renderers/FooterLinkList').then((mod) => mod.FooterLinkList)
)
const MarketSwitcher = dynamic(async () =>
  import('@/components/renderers/MarketSwitcher').then((mod) => mod.MarketSwitcher)
)
const SocialMediaIcons = dynamic(async () =>
  import('@/components/renderers/SocialMediaIcons').then((mod) => mod.SocialMediaIcons)
)
const Marquee = dynamic(
  async () => import('@/components/renderers/Marquee').then((mod) => mod.Marquee),
  { loading: () => <Spinner /> }
)
const ReviewCard = dynamic(async () =>
  import('@/components/renderers/ReviewCard').then((mod) => mod.ReviewCard)
)
const TrustPilotWidget = dynamic(async () =>
  import('@/components/renderers/TrustpilotWidget').then((mod) => mod.TrustpilotWidget)
)
const GoogleWidget = dynamic(async () =>
  import('@/components/renderers/GoogleWidget').then((mod) => mod.GoogleWidget)
)
const InlineColumns = dynamic(async () =>
  import('@/components/renderers/InlineColumns').then((mod) => mod.InlineColumns)
)
const ProductInfoAccordion = dynamic(async () =>
  import('@/components/renderers/ProductInfoAccordion').then((mod) => mod.ProductInfoAccordion)
)
const RadioCardSet = dynamic(async () =>
  import('@/components/renderers/RadioCardSet').then((mod) => mod.RadioCardSet)
)
const AccordionList = dynamic(async () =>
  import('@/components/renderers/AccordionList').then((mod) => mod.AccordionList)
)
const InformationList = dynamic(async () =>
  import('@/components/renderers/InformationList').then((mod) => mod.InformationList)
)
const TableRenderer = dynamic(async () =>
  import('@/components/renderers/TableRenderer').then((mod) => mod.TableRenderer)
)
const LegalTextElement = dynamic(async () =>
  import('@/components/renderers/LegalTextElement').then((mod) => mod.LegalTextElement)
)
const Iframe = dynamic(async () =>
  import('@/components/renderers/Iframe').then((mod) => mod.Iframe)
)
const ProductCardRenderer = dynamic(async () =>
  import('@/components/renderers/ProductCardRenderer').then((mod) => mod.ProductCardRenderer)
)
const Question = dynamic(async () =>
  import('@/components/renderers/Question').then((mod) => mod.Question)
)
const YoutubeVideo = dynamic(async () =>
  import('@/components/renderers/YoutubeVideo').then((mod) => mod.YoutubeVideo)
)
const StreamableVideo = dynamic(async () =>
  import('@/components/renderers/StreamableVideo').then((mod) => mod.StreamableVideo)
)
const ImageListSwitcher = dynamic(async () =>
  import('@/components/renderers/ImageListSwitcher').then((mod) => mod.ImageListSwitcher)
)
const NavigationItem = dynamic(async () =>
  import('@/components/renderers/NavigationItem').then((mod) => mod.NavigationItem)
)
const ListItem = dynamic(async () =>
  import('@/components/renderers/ListItem').then((mod) => mod.ListItem)
)
const ImageLine = dynamic(async () =>
  import('@/components/renderers/ImageLine').then((mod) => mod.ImageLine)
)
const ImageRenderer = dynamic(async () =>
  import('@/components/renderers/ImageRenderer').then((mod) => mod.ImageRenderer)
)
const ProductList = dynamic(async () =>
  import('@/components/renderers/ProductList').then((mod) => mod.ProductList)
)
const MultiColoredImage = dynamic(async () =>
  import('@/components/renderers/MultiColoredImage').then((mod) => mod.MultiColoredImage)
)
const LiabilityConversationStarterButton = dynamic(async () =>
  import(
    '@/components/renderers/LiabilityConversationStarterButtonRenderer/LiabilityConversationStarterButtonRenderer'
  ).then((mod) => mod.LiabilityConversationStarterButton)
)
const ContentsConversationStarter = dynamic(async () =>
  import(
    '@/components/renderers/ContentsConversationStarterRenderer/ContentsConversationStarterRenderer'
  ).then((mod) => mod.ContentsConversationStarter)
)
const InfoNote = dynamic(async () =>
  import('@/components/renderers/InfoNote').then((mod) => mod.InfoNote)
)
const Alert = dynamic(async () => import('@/components/renderers/Alert').then((mod) => mod.Alert))

const ProductCardSimple = dynamic(async () =>
  import('@/components/renderers/ProductCardSimple').then((mod) => mod.ProductCardSimple)
)

type Props = {
  blocks: unknown[]
  align?: HorizontalAlignment
  space?: Space
  width: number
  sectionName: string
  contentColor?: Color
  backgroundColor?: Color
  reverseOrderForWideScreen?: boolean
  hasPriority?: boolean
}

type LocalizedText = { [key in Language]: string }

const OrderedList: PortableTextListComponent = (props) => (
  <ol className="list-inside list-decimal pb-4 pl-4">{props.children}</ol>
)
const UnorderedList: PortableTextListComponent = (props) => (
  <ul className="list-inside list-disc pb-4 pl-2.5">{props.children}</ul>
)
const ListItemText: PortableTextListItemComponent = (props) => (
  <li
    className={body1({
      className: 'lg:leading-11 [&>p]:inline-block',
    })}
  >
    {props.children}
  </li>
)

const container = tv({
  base: 'z-10 flex h-full w-full flex-col',
  variants: {
    align: {
      left: 'items-start',
      center: 'items-center',
      right: 'items-end',
    },
    space: {
      none: '[&>*:not(:last-child)]:mb-0',
      xxsmall: '[&>*:not(:last-child)]:mb-1',
      mini: '[&>*:not(:last-child)]:mb-2',
      xsmall: '[&>*:not(:last-child)]:mb-2.5',
      small: '[&>*:not(:last-child)]:mb-4',
      medium: '[&>*:not(:last-child)]:mb-5',
      gutter: '[&>*:not(:last-child)]:mb-6',
      large: '[&>*:not(:last-child)]:mb-8',
      xlarge: '[&>*:not(:last-child)]:mb-12',
      xlargeMedium: '[&>*:not(:last-child)]:mb-16',
      xxlarge: '[&>*:not(:last-child)]:mb-20',
      xxlargeMedium: '[&>*:not(:last-child)]:mb-28',
      xxxlarge: '[&>*:not(:last-child)]:mb-40',
    },
  },
  defaultVariants: {
    space: 'medium',
  },
})

export const markLinkStyle = tv({
  base: 'inline-flex cursor-pointer items-center gap-x-0.5 text-brand-dark no-underline',
  variants: {
    hasIcon: {
      true: 'underline',
    },
    hasModal: {
      true: 'underline',
    },
  },
})

// TODO: Move to ui-kit
const ShareIcon = (props: SvgProps) => (
  <Svg width={props.width ?? 16} height={props.height ?? 16} fill="none" {...props}>
    <G stroke="#003D2B" strokeLinecap="round" strokeLinejoin="round" clipPath="url(#a)">
      <Path d="M7.222 9.402 15 1.75M15 6.123V1.75h-4.444M8.82 4.528H2.985a.486.486 0 0 0-.486.486v8.75a.486.486 0 0 0 .486.486h8.75a.486.486 0 0 0 .486-.486V7.93" />
    </G>
    <Defs>
      <ClipPath id="a">
        <Path fill="#fff" d="M2.083 1.333h13.333v13.333H2.083z" />
      </ClipPath>
    </Defs>
  </Svg>
)

const MarkLink = (props: {
  sectionName: string
  node: PortableTextMarkComponentProps<{ _type: 'link'; href: string; hasIcon?: boolean }>
}) => {
  const { track } = useAnalytics()
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false)
  if (!props.node.value?.href) {
    return null
  }
  const href = props.node.value.href
  const shouldOpenInNewTab = !href.includes(DOMAIN) || href.includes('.pdf')
  const shouldNoFollow = href.includes('.pdf')
  const parsedUrl = z.string().url().safeParse(href)
  if (href.includes('calendly.com')) {
    return (
      <>
        <PopupModal
          url={href}
          rootElement={
            (typeof window !== 'undefined'
              ? document.getElementById('__next')
              : null) as HTMLElement
          }
          onModalClose={() => {
            setIsCalendlyOpen(false)
          }}
          open={isCalendlyOpen}
        />
        <button
          className={markLinkStyle({
            hasIcon: props.node.value?.hasIcon,
          })}
          onClick={() => {
            track(EVENTS.LinkClicked, {
              href,
              children: JSON.stringify(props.node.children),
              sectionName: props.sectionName,
            })
            setIsCalendlyOpen(true)
          }}
        >
          <span>{props.node.children}</span>
          {props.node.value?.hasIcon ? <ShareIcon /> : null}
        </button>
      </>
    )
  }
  return (
    <Link
      className={markLinkStyle({
        hasIcon: props.node.value?.hasIcon,
      })}
      rel={shouldNoFollow ? 'nofollow' : ''}
      href={href}
      target={parsedUrl.success && shouldOpenInNewTab ? '_blank' : undefined}
      onClick={() => {
        track(EVENTS.LinkClicked, {
          href,
          children: props.node.children,
          sectionName: props.sectionName,
        })
      }}
    >
      <span>{props.node.children}</span>
      {props.node.value?.hasIcon ? <ShareIcon /> : null}
    </Link>
  )
}

export const BlockContent = (props: Props) => {
  const formMethods = useFormContext()
  const { locale } = useRouter()
  const [language] = getLocaleParts(locale)
  const form = useForm()
  const align = props.align || 'left'
  const components = useMemo(() => {
    return {
      block: (node: PortableTextComponentProps<PortableTextBlock>) => (
        <Block align={align} node={node} />
      ),
      marks: {
        actionLink: (node: PortableTextMarkComponentProps<{ _type: 'actionLink'; url: string }>) =>
          node.value?.url ? (
            <LinkRendererSimple
              text={node.text}
              url={node.value.url}
              sectionName={props.sectionName}
            />
          ) : null,
        link: (
          node: PortableTextMarkComponentProps<{ _type: 'link'; href: string; hasIcon?: boolean }>
        ) => {
          return <MarkLink node={node} sectionName={props.sectionName} />
        },
        modalLink: (
          node: PortableTextMarkComponentProps<{
            _type: 'modalLink'
            content: unknown[]
          }>
        ) => {
          return <ModalLink node={node} sectionName={props.sectionName} />
        },
      },
      list: {
        number: OrderedList,
        bullet: UnorderedList,
      },
      listItem: ListItemText,
      types: {
        appDownloadButtons: (node: PortableTextTypeComponentProps<unknown>) => (
          <AppDownloadButtons sectionName={props.sectionName} node={node} />
        ),
        configurableTypography: (node: PortableTextTypeComponentProps<unknown>) => (
          <ConfigurableTypography node={node} align={align} pageColor={props.contentColor} />
        ),
        footerLinkList: (node: PortableTextTypeComponentProps<unknown>) => (
          <FooterLinkList
            node={node}
            pageColor={props.contentColor}
            sectionName={props.sectionName}
          />
        ),
        flexibleImage: (node: PortableTextTypeComponentProps<unknown>) => (
          <FlexibleImage
            hasPriority={props.hasPriority ?? false}
            containerWidth={props.width}
            node={node}
            itemsCount={props.blocks?.length ?? 0}
          />
        ),
        marketSwitcher: () => (
          <MarketSwitcher
            pageColor={props.contentColor}
            backgroundColor={props.backgroundColor}
            width={props.width}
          />
        ),
        socialMediaIcons: () => <SocialMediaIcons pageColor={props.contentColor} />,
        multiColoredImage: (node: PortableTextTypeComponentProps<unknown>) => (
          <MultiColoredImage node={node} />
        ),
        productList: (node: PortableTextTypeComponentProps<unknown>) => (
          <ProductList node={node} sectionName={props.sectionName} />
        ),
        navigationItem: (node: PortableTextTypeComponentProps<unknown>) => (
          <NavigationItem node={node} sectionName={props.sectionName} />
        ),
        spacer: (node: PortableTextTypeComponentProps<unknown>) => (
          <Spacer node={node} defaultSpace={props.space} />
        ),
        button: (node: PortableTextTypeComponentProps<unknown>) => (
          <ButtonRenderer
            node={node}
            align={align}
            sectionName={props.sectionName}
            reverseOrderForWideScreen={props.reverseOrderForWideScreen}
          />
        ),
        marquee: (node: PortableTextTypeComponentProps<unknown>) => (
          <Marquee node={node} sectionName={props.sectionName} contentWidth={props.width} />
        ),
        infoNote: (node: PortableTextTypeComponentProps<unknown>) => (
          <InfoNote node={node} sectionName={props.sectionName} align={align} width={props.width} />
        ),
        alert: (node: PortableTextTypeComponentProps<unknown>) => <Alert node={node} />,
        reviewCard: (node: PortableTextTypeComponentProps<unknown>) => <ReviewCard node={node} />,
        restrictedContent: (node: PortableTextTypeComponentProps<unknown>) => (
          <RestrictedContent
            node={node}
            sectionName={props.sectionName}
            width={props.width}
            align={align}
          />
        ),
        image: (node: PortableTextTypeComponentProps<unknown>) => (
          <ImageRenderer
            node={node}
            containerWidth={props.width}
            hasPriority={props.hasPriority ?? false}
          />
        ),
        localeImage: (node: PortableTextTypeComponentProps<unknown>) => {
          return (
            match(node.value)
              // TODO: clean up france mapping in https://hellogetsafe.atlassian.net/browse/IX-2499
              .with(
                { en: P.not(P.nullish), de: P.not(P.nullish), fr: P.optional('fr') },
                (image) => (
                  <ImageRenderer
                    node={{
                      ...node,
                      value: image[language],
                    }}
                    containerWidth={props.width}
                    hasPriority={props.hasPriority ?? false}
                  />
                )
              )
              .otherwise(() => <Text>Locale image not set correctly</Text>)
          )
        },
        imageLine: (node: PortableTextTypeComponentProps<unknown>) => (
          <ImageLine node={node} width={props.width} />
        ),
        trustPilotWidget: (node: PortableTextTypeComponentProps<unknown>) => (
          <TrustPilotWidget node={node} width={props.width} />
        ),
        googleWidget: (node: PortableTextTypeComponentProps<unknown>) => (
          <GoogleWidget node={node} />
        ),
        inlineColumns: (node: PortableTextTypeComponentProps<unknown>) => (
          <InlineColumns
            node={node}
            width={props.width}
            alignHorizontallyDefault={align}
            spaceDefault={props.space ?? 'medium'}
            sectionName={props.sectionName}
          />
        ),
        card: (node: PortableTextTypeComponentProps<unknown>) => (
          <Card node={node} align={align} sectionName={props.sectionName} />
        ),
        ctaButton: (node: PortableTextTypeComponentProps<unknown>) => <CtaButton node={node} />,
        productInfoAccordion: (node: PortableTextTypeComponentProps<unknown>) => (
          <ProductInfoAccordion node={node} sectionName={props.sectionName} />
        ),
        link: (node: PortableTextTypeComponentProps<unknown>) => (
          <LinkRenderer node={node} sectionName={props.sectionName} />
        ),
        radioCardSet: (node: PortableTextTypeComponentProps<unknown>) => (
          <RadioCardSet
            node={node}
            align={align}
            sectionName={props.sectionName}
            width={props.width}
          />
        ),
        accordionList: (node: PortableTextTypeComponentProps<unknown>) => (
          <AccordionList
            node={node}
            align={align}
            sectionName={props.sectionName}
            width={props.width}
            sectionColor={props.backgroundColor}
          />
        ),
        checkmarkList: (node: PortableTextTypeComponentProps<unknown>) => (
          <CheckMarkList node={node} />
        ),
        informationList: (node: PortableTextTypeComponentProps<unknown>) => (
          <InformationList node={node} />
        ),
        carousel: (node: PortableTextTypeComponentProps<unknown>) => (
          <Carousel node={node} width={props.width} />
        ),
        tableRenderer: (node: PortableTextTypeComponentProps<unknown>) => (
          <TableRenderer node={node} />
        ),
        chipRow: (node: PortableTextTypeComponentProps<unknown>) => (
          <ChipsRow node={node} sectionName={props.sectionName} />
        ),
        legalTextElement: (node: PortableTextTypeComponentProps<unknown>) => (
          <LegalTextElement node={node} width={props.width} sectionName={props.sectionName} />
        ),
        productCard: (node: PortableTextTypeComponentProps<unknown>) => (
          <ProductCardRenderer node={node} sectionName={props.sectionName} width={props.width} />
        ),
        iframe: (node: PortableTextTypeComponentProps<unknown>) => <Iframe node={node} />,
        question: (node: PortableTextTypeComponentProps<unknown>) => <Question node={node} />,
        urlAnchorTarget: (node: PortableTextTypeComponentProps<{ slug: { current: string } }>) => (
          // The only purpose of this element is to create an element with an id
          // for anchors to be able to target a specific section
          <div style={{ top: -70, position: 'relative' }} id={node.value.slug.current} />
        ),
        youtubeVideo: (node: PortableTextTypeComponentProps<unknown>) => (
          <YoutubeVideo node={node} />
        ),
        streamable: (node: PortableTextTypeComponentProps<unknown>) => (
          <StreamableVideo node={node} sectionName={props.sectionName} />
        ),
        imageListSwitcher: (node: PortableTextTypeComponentProps<unknown>) => (
          <ImageListSwitcher node={node} width={props.width} />
        ),
        listItem: (node: PortableTextTypeComponentProps<unknown>) => (
          <ListItem node={node} sectionName={props.sectionName} />
        ),
        liabilityConversationStarterButton: (node: PortableTextTypeComponentProps<unknown>) => (
          <LiabilityConversationStarterButton node={node} sectionName={props.sectionName} />
        ),
        contentsConversationStarter: (node: PortableTextTypeComponentProps<unknown>) => (
          <ContentsConversationStarter node={node} sectionName={props.sectionName} />
        ),
        productCardSimple: (
          node: PortableTextTypeComponentProps<
            Partial<ProductCardSimpleSchema> & {
              title: LocalizedText
              description: LocalizedText
              primaryAction: { [key in Language]: ProductCardSimpleSchema['primaryAction'] }
              secondaryAction: { [key in Language]: ProductCardSimpleSchema['secondaryAction'] }
            }
          >
        ) => {
          const updatedNodeValue = match(node.value)
            .with(P.not(P.nullish), (value) => {
              return {
                ...value,
                title: value.title[language],
                description: value.description[language],
                primaryAction: value.primaryAction[language],
                secondaryAction: value.secondaryAction[language],
              }
            })
            .otherwise(() => node.value)

          return (
            <ProductCardSimple
              node={{
                ...node,
                value: updatedNodeValue,
              }}
            />
          )
        },
      },
      unknownType: (node: PortableTextComponentProps<UnknownNodeType>) => {
        return <div>Rendering of {node.value._type} not implemented yet</div>
      },
    }
  }, [align, props, language])

  return (
    // In some cases there is no form context, and we need to use a form to render the content that depends on forms
    <FormProvider {...(formMethods ? formMethods : form)}>
      <div
        className={container({
          align,
          space: props.space,
        })}
      >
        <PortableText
          value={(props.blocks ?? []) as (PortableTextBlock | ArbitraryTypedObject)[]}
          components={components}
          onMissingComponent={reportErrorMessage}
        />
      </div>
    </FormProvider>
  )
}
